/**
 * Shared appointment management flow: floating details, confirm/cancel/delete mutations,
 * and transitions into manual reschedule.
 */

import { useCallback, useLayoutEffect, useState } from 'react';
import { App, Modal } from 'antd';
import { autoUpdate, flip, offset, shift, useFloating, type Middleware, type ReferenceType } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import {
  useCancelAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentMutation,
  type Appointment,
  type UpdateAppointmentRequest,
} from '@/api/mcp-appointments';
import { formatAppointmentDateTime, normalizeDetailsReference } from '../calendar/calendarUtils';
import { getApiErrorMessage } from '@/utils/apiError';

/** Active overlay phase for appointment management UI. */
export type AppointmentManagePhase = 'none' | 'manualReschedule';

/** Mutation kinds surfaced to callers after a successful manage action. */
export type AppointmentMutateAction = 'confirm' | 'cancel' | 'delete' | 'noShow';

/** Optional hooks and feature flags for `useAppointmentManageFlow`. */
interface UseAppointmentManageFlowOptions {
  /** When `true`, exposes admin delete handling from the details popup. */
  enableDelete?: boolean;
  /** Called after confirm, cancel, or delete succeeds. */
  onMutated?: (action: AppointmentMutateAction, appointment: Appointment) => void | Promise<void>;
  /** Called when the manual reschedule overlay closes. */
  onManualRescheduleClose?: () => void | Promise<void>;
  /** Overrides Floating UI middleware used to position the details popup. */
  floatingMiddleware?: Middleware[];
}

/** Public shape returned by `useAppointmentManageFlow`. */
export type AppointmentManageFlow = ReturnType<typeof useAppointmentManageFlow>;

/**
 * Owns appointment details positioning, mutation confirmations, and manage-phase transitions.
 * @returns Active appointment state, floating-ui refs/styles, and action handlers for overlays.
 */
export function useAppointmentManageFlow(options: UseAppointmentManageFlowOptions = {}) {
  const { enableDelete = false, onMutated, onManualRescheduleClose, floatingMiddleware } = options;
  const { t, i18n } = useTranslation('manage');
  const { t: tCommon } = useTranslation('common');
  const { message } = App.useApp();
  const updateAppointmentMutation = useUpdateAppointmentMutation();
  const cancelAppointmentMutation = useCancelAppointmentMutation();
  const deleteAppointmentMutation = useDeleteAppointmentMutation();
  const [activeAppointment, setActiveAppointment] = useState<Appointment | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsReference, setDetailsReference] = useState<ReferenceType | null>(null);
  const [detailsAnchorKey, setDetailsAnchorKey] = useState(0);
  const [managePhase, setManagePhase] = useState<AppointmentManagePhase>('none');
  const [confirmingAppointmentId, setConfirmingAppointmentId] = useState<string | null>(null);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState<string | null>(null);
  const [markingNoShowAppointmentId, setMarkingNoShowAppointmentId] = useState<string | null>(null);
  const [savingNotesAppointmentId, setSavingNotesAppointmentId] = useState<string | null>(null);

  const pendingAppointmentId = confirmingAppointmentId || cancellingAppointmentId || markingNoShowAppointmentId;

  /** Localized " with {name}" fragment for the confirmation texts, empty when the client is unknown. */
  const withClientName = useCallback(
    (appointment: Appointment) => {
      const name = appointment.anonymousName?.trim();
      return name ? t('calendar.messages.withClient', { name }) : '';
    },
    [t],
  );

  const { refs, floatingStyles, isPositioned, update } = useFloating({
    open: detailsOpen,
    onOpenChange: setDetailsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware ?? [offset(8), flip(), shift({ padding: 8 })],
  });

  // The details anchor is a virtual element (see normalizeDetailsReference); floating-ui only accepts
  // DOM elements via `elements.reference`, so virtual anchors are applied as the position reference.
  useLayoutEffect(() => {
    if (!detailsOpen || !detailsReference) {
      return;
    }

    refs.setPositionReference(detailsReference);
    update();
  }, [detailsAnchorKey, detailsOpen, detailsReference, refs, update]);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsReference(null);
  }, []);

  const closeFlow = useCallback(() => {
    setDetailsOpen(false);
    setManagePhase('none');
    setActiveAppointment(null);
    setDetailsReference(null);
  }, []);

  const openDetails = useCallback(
    (appointment: Appointment, reference?: ReferenceType | null) => {
      setDetailsReference(normalizeDetailsReference(reference));
      setDetailsAnchorKey((key) => key + 1);
      setActiveAppointment(appointment);
      setManagePhase('none');
      setDetailsOpen(true);
    },
    [],
  );

  const closeManualReschedule = useCallback(() => {
    setManagePhase('none');
    void onManualRescheduleClose?.();
    setActiveAppointment(null);
    setDetailsReference(null);
  }, [onManualRescheduleClose]);

  const requestManualReschedule = useCallback(
    (appointment: Appointment) => {
      setActiveAppointment(appointment);
      setDetailsOpen(false);
      setManagePhase('manualReschedule');
      setDetailsReference(null);
    },
    [],
  );

  const confirmAppointment = useCallback(
    (appointment: Appointment) => {
      Modal.confirm({
        title: t('calendar.messages.confirmTitle'),
        content: t('calendar.messages.confirmContent', {
          name: withClientName(appointment),
          date: formatAppointmentDateTime(appointment.startTime, i18n.language),
        }),
        okText: t('calendar.messages.confirmOk'),
        okType: 'primary',
        cancelText: tCommon('actions.cancel'),
        onOk: async () => {
          setConfirmingAppointmentId(appointment.id);
          try {
            const updateBody: UpdateAppointmentRequest = { status: 'CONFIRMED' };
            await updateAppointmentMutation.mutateAsync({
              appointmentId: appointment.id,
              body: updateBody,
            });
            message.success(t('calendar.messages.confirmSuccess'));
            await onMutated?.('confirm', appointment);
            closeFlow();
          } catch (error) {
            message.error(t('calendar.messages.confirmError'));
            console.error('Failed to confirm appointment:', error);
          } finally {
            setConfirmingAppointmentId(null);
          }
        },
      });
    },
    [message, onMutated, t, tCommon, updateAppointmentMutation, closeFlow, withClientName, i18n.language],
  );

  const cancelAppointment = useCallback(
    (appointment: Appointment) => {
      Modal.confirm({
        title: t('calendar.messages.cancelTitle'),
        content: t('calendar.messages.cancelContent', {
          name: withClientName(appointment),
          date: formatAppointmentDateTime(appointment.startTime, i18n.language),
        }),
        okText: t('calendar.messages.cancelOk'),
        okType: 'primary',
        cancelText: tCommon('actions.back'),
        onOk: async () => {
          setCancellingAppointmentId(appointment.id);
          try {
            await cancelAppointmentMutation.mutateAsync(appointment.id);
            message.success(t('calendar.messages.cancelSuccess'));
            await onMutated?.('cancel', appointment);
            closeFlow();
          } catch (error: unknown) {
            message.error(getApiErrorMessage(error, t('calendar.messages.cancelError')));
            console.error('Failed to cancel appointment:', error);
          } finally {
            setCancellingAppointmentId(null);
          }
        },
      });
    },
    [cancelAppointmentMutation, closeFlow, message, onMutated, t, tCommon, withClientName, i18n.language],
  );

  const deleteAppointment = useCallback(
    (appointment: Appointment) => {
      Modal.confirm({
        title: t('calendar.messages.deleteTitle'),
        content: t('calendar.messages.deleteContent', {
          name: withClientName(appointment),
          date: formatAppointmentDateTime(appointment.startTime, i18n.language),
        }),
        okText: t('calendar.messages.deleteOk'),
        okType: 'danger',
        cancelText: tCommon('actions.cancel'),
        onOk: async () => {
          try {
            await deleteAppointmentMutation.mutateAsync(appointment.id);
            message.success(t('calendar.messages.deleteSuccess'));
            await onMutated?.('delete', appointment);
            closeFlow();
          } catch (error) {
            message.error(t('calendar.messages.deleteError'));
            console.error('Failed to delete appointment:', error);
          }
        },
      });
    },
    [closeFlow, deleteAppointmentMutation, message, onMutated, t, tCommon, withClientName, i18n.language],
  );

  const handleConfirmFromDetails = useCallback(
    (appointment: Appointment) => {
      closeDetails();
      confirmAppointment(appointment);
    },
    [closeDetails, confirmAppointment],
  );

  const handleCancelFromDetails = useCallback(
    (appointment: Appointment) => {
      closeDetails();
      cancelAppointment(appointment);
    },
    [cancelAppointment, closeDetails],
  );

  const handleDeleteFromDetails = useCallback(
    (appointment: Appointment) => {
      closeDetails();
      deleteAppointment(appointment);
    },
    [closeDetails, deleteAppointment],
  );

  const markNoShowAppointment = useCallback(
    (appointment: Appointment) => {
      Modal.confirm({
        title: t('calendar.messages.noShowTitle'),
        content: t('calendar.messages.noShowContent', {
          name: withClientName(appointment),
          date: formatAppointmentDateTime(appointment.startTime, i18n.language),
        }),
        okText: t('calendar.messages.noShowOk'),
        okType: 'primary',
        cancelText: tCommon('actions.back'),
        onOk: async () => {
          setMarkingNoShowAppointmentId(appointment.id);
          try {
            const updateBody: UpdateAppointmentRequest = { status: 'NO_SHOW' };
            await updateAppointmentMutation.mutateAsync({
              appointmentId: appointment.id,
              body: updateBody,
            });
            message.success(t('calendar.messages.noShowSuccess'));
            await onMutated?.('noShow', appointment);
            closeFlow();
          } catch (error: unknown) {
            message.error(getApiErrorMessage(error, t('calendar.messages.noShowError')));
            console.error('Failed to mark appointment as no-show:', error);
          } finally {
            setMarkingNoShowAppointmentId(null);
          }
        },
      });
    },
    [closeFlow, message, onMutated, t, tCommon, updateAppointmentMutation, withClientName, i18n.language],
  );

  const handleMarkNoShowFromDetails = useCallback(
    (appointment: Appointment) => {
      closeDetails();
      markNoShowAppointment(appointment);
    },
    [closeDetails, markNoShowAppointment],
  );

  /**
   * Saves the staff note of an appointment and refreshes the open details popup in place.
   * @returns `true` when the note was saved.
   */
  const saveAppointmentNotes = useCallback(
    async (appointment: Appointment, notes: string): Promise<boolean> => {
      setSavingNotesAppointmentId(appointment.id);
      try {
        const updated = await updateAppointmentMutation.mutateAsync({
          appointmentId: appointment.id,
          body: { notes: notes.trim() ? notes : null },
        });
        setActiveAppointment((current) =>
          current && current.id === appointment.id ? { ...current, notes: updated.notes } : current,
        );
        message.success(t('appointmentComponents.details.notesSection.saved'));
        return true;
      } catch (error: unknown) {
        message.error(getApiErrorMessage(error, t('appointmentComponents.details.notesSection.saveError')));
        console.error('Failed to save appointment notes:', error);
        return false;
      } finally {
        setSavingNotesAppointmentId(null);
      }
    },
    [message, t, updateAppointmentMutation],
  );

  return {
    activeAppointment,
    detailsOpen,
    detailsPositioned: isPositioned,
    managePhase,
    pendingAppointmentId,
    refs,
    floatingStyles,
    openDetails,
    closeDetails,
    closeFlow,
    requestManualReschedule,
    closeManualReschedule,
    confirmAppointment: handleConfirmFromDetails,
    cancelAppointment: handleCancelFromDetails,
    markNoShowAppointment: handleMarkNoShowFromDetails,
    deleteAppointment: enableDelete ? handleDeleteFromDetails : undefined,
    saveAppointmentNotes,
    savingNotesAppointmentId,
  };
}
